import * as React from "react";
import { useNavigate, Link } from "react-router";
import { useList } from "@refinedev/core";
import type { CrudFilter } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { CursorPagination } from "@/components/CursorPagination";
import { SubscriptionStatusPill } from "@/components/SubscriptionStatusPill";
import { AccountStatusPill } from "@/components/AccountStatusPill";
import type { SubscriptionStatus } from "@/components/SubscriptionStatusPill";
import type { AccountStatus } from "@/components/AccountStatusPill";

export type AdminUser = {
  id: string;
  email: string;
  subscriptionStatus: SubscriptionStatus;
  accountStatus: AccountStatus;
  registeredAt: string;
};

function formatRegisteredAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface UserRowProps {
  user: AdminUser;
  onClick: (id: string) => void;
}

export function UserRow({ user, onClick }: UserRowProps): React.JSX.Element {
  function handleKeyDown(e: React.KeyboardEvent<HTMLTableRowElement>): void {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(user.id);
    }
  }

  return (
    <tr
      className="border-b border-[var(--border)] last:border-b-0 min-h-[44px] transition-colors hover:bg-[var(--accent-weak)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
      onClick={() => onClick(user.id)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Открыть пользователя ${user.email}`}
    >
      <td
        className="px-3.5 py-2.5 font-mono text-[12px] text-[var(--muted)] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
        title={user.id}
      >
        <Link
          to={`/users/show/${user.id}`}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded block"
          tabIndex={0}
        >
          {user.id}
        </Link>
      </td>
      <td className="px-3.5 py-2.5 text-[13px] font-[500]">{user.email}</td>
      <td className="px-3.5 py-2.5 w-[110px]">
        <SubscriptionStatusPill status={user.subscriptionStatus} />
      </td>
      <td className="px-3.5 py-2.5 w-[140px]">
        <AccountStatusPill status={user.accountStatus} />
      </td>
      <td className="px-3.5 py-2.5 w-[140px] whitespace-nowrap text-[var(--muted)] text-[12px]">
        {formatRegisteredAt(user.registeredAt)}
      </td>
    </tr>
  );
}

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export function UsersList(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchEmail, setSearchEmail] = React.useState("");
  const [subscriptionFilter, setSubscriptionFilter] = React.useState("");
  const [accountStatusFilter, setAccountStatusFilter] = React.useState("");
  const debouncedEmail = useDebounce(searchEmail, 300);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [rows, setRows] = React.useState<AdminUser[]>([]);

  const refineFilters: CrudFilter[] = [];
  if (debouncedEmail) {
    refineFilters.push({ field: "email", operator: "eq", value: debouncedEmail });
  }
  if (subscriptionFilter) {
    refineFilters.push({
      field: "subscriptionStatus",
      operator: "eq",
      value: subscriptionFilter,
    });
  }
  if (accountStatusFilter) {
    refineFilters.push({
      field: "accountStatus",
      operator: "eq",
      value: accountStatusFilter,
    });
  }

  const { query } = useList<AdminUser>({
    resource: "users",
    filters: refineFilters,
    meta: { cursor },
  });

  const data = query.data;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const refetch = query.refetch;

  React.useEffect(() => {
    if (data?.data) {
      const incoming = data.data as AdminUser[];
      if (!cursor) {
        setRows(incoming);
      } else {
        setRows((prev) => {
          const seen = new Set(prev.map((u) => u.id));
          return [...prev, ...incoming.filter((u) => !seen.has(u.id))];
        });
      }
    }
  }, [data?.data, cursor]);

  const nextCursor =
    (data as { meta?: { nextCursor?: string | null } } | undefined)?.meta?.nextCursor ?? null;

  const handleSearchChange = (value: string) => {
    setSearchEmail(value);
    setCursor(undefined);
    setRows([]);
  };

  const handleFilterChange = (field: "subscription" | "accountStatus", value: string) => {
    if (field === "subscription") setSubscriptionFilter(value);
    else setAccountStatusFilter(value);
    setCursor(undefined);
    setRows([]);
  };

  const handleRowClick = (id: string) => {
    void navigate(`/users/show/${id}`);
  };

  const showEmpty = !isLoading && !isError && rows.length === 0;
  const showTable = !isLoading && rows.length > 0;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="px-6 pt-5 pb-0">
        <h1 className="text-[22px] font-[700] tracking-[-0.3px]">Пользователи</h1>
      </div>

      <div className="sticky top-0 z-[5] bg-[var(--surface)] border-b border-[var(--border)] px-6 py-3 flex items-center gap-3">
        <div className="relative flex-[0_0_320px]">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 stroke-[var(--muted)] stroke-2 fill-none pointer-events-none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            aria-label="Поиск по email"
            placeholder="Поиск по email…"
            value={searchEmail}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full h-9 pl-8 pr-3 border border-[var(--border)] rounded-full text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none transition-colors placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:bg-[var(--surface)]"
          />
        </div>

        <select
          aria-label="Фильтр по подписке"
          value={subscriptionFilter}
          onChange={(e) => handleFilterChange("subscription", e.target.value)}
          className="h-9 px-3 pr-8 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] cursor-pointer outline-none focus:border-[var(--accent)] appearance-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='rgba(0,0,0,0.52)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 10px center",
          }}
        >
          <option value="">Подписка: все</option>
          <option value="active">Premium</option>
          <option value="none">Free</option>
          <option value="expired">Истекла</option>
        </select>

        <select
          aria-label="Фильтр по статусу аккаунта"
          value={accountStatusFilter}
          onChange={(e) => handleFilterChange("accountStatus", e.target.value)}
          className="h-9 px-3 pr-8 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] cursor-pointer outline-none focus:border-[var(--accent)] appearance-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='rgba(0,0,0,0.52)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 10px center",
          }}
        >
          <option value="">Статус: все</option>
          <option value="active">Активен</option>
          <option value="banned">Заблокирован</option>
        </select>

        {!isLoading && !isError && rows.length > 0 && (
          <span className="ml-auto text-[13px] text-[var(--muted)] whitespace-nowrap">
            Найдено: {rows.length}
          </span>
        )}
      </div>

      <div className="px-6 pb-6 flex-1">
        {isLoading && (
          <div className="mt-3 bg-[var(--surface)] rounded-[var(--radius)] border border-[var(--border)] overflow-hidden">
            <SkeletonTable columns={5} rows={6} />
          </div>
        )}

        {isError && (
          <div className="mt-3">
            <ErrorBanner
              message="Не удалось загрузить список пользователей. Проверьте подключение к сети и попробуйте снова."
              onRetry={() => void refetch()}
            />
          </div>
        )}

        {showEmpty && (
          <EmptyState
            message={
              debouncedEmail
                ? "Пользователи не найдены"
                : "Введите email для поиска"
            }
          />
        )}

        {showTable && (
          <table className="w-full border-collapse mt-3 bg-[var(--surface)] rounded-[var(--radius)] overflow-hidden border border-[var(--border)]">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th
                  scope="col"
                  className="w-[120px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="w-[110px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Подписка
                </th>
                <th
                  scope="col"
                  className="w-[140px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Статус
                </th>
                <th
                  scope="col"
                  className="w-[140px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Дата регистрации
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <UserRow key={user.id} user={user} onClick={handleRowClick} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CursorPagination
        nextCursor={nextCursor}
        onLoadMore={() => setCursor(nextCursor ?? undefined)}
        isLoading={isLoading}
      />
    </div>
  );
}
